<script setup lang="ts">
/**
 * B.45 P1 — 渐进式启用一级菜单 sidebar(168px)。
 *
 * 区块(自上而下):
 *   1. 当前档口卡(shop_name + plan badge)—— 点击展开切换面板(dev 工具)
 *   2. 一级菜单(顺序如下)+ 二级折叠
 *      · 产品图库 (Layer 1, 默认启用)
 *      · 商品管理 (Layer 2, business)
 *      · 小程序管理 (Layer 3, miniapp)
 *      · 自媒体助手 (媒体附加, flat 一级)
 *      · 智能客服 / 电子宠物 (非路由按钮:开右侧抽屉 / 开关螃蟹;代登录时隐藏)
 *      · 系统设置 (always, 排最末 —— 在上面两个按钮之后)
 *   3. 底部账号 / 登录栏
 *
 * 未开通 capability 的一级菜单:**不隐藏**,加 ti-lock + 仍可展开/进入,
 * 进去看到 UpgradePlaceholder 的"未开通+联系销售"。激发付费升级。
 *
 * "切档口 ulid"面板 dev 期保留;B.45 P4 接 JWT 登录后退役。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAuthModal } from '@/composables/useAuthModal'
import { useSupport } from '@/composables/useSupport'
import { usePet } from '@/composables/usePet'
import { armMediaFlowHint } from '@/composables/useMediaFlowHint'
import UpgradeModal from '@/components/UpgradeModal.vue'
import type { CapabilityName } from '@/api/me'

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { openAuth } = useAuthModal()
const { openSupport } = useSupport()
const { petEnabled, togglePet } = usePet()

const hasPetCap = computed(() => auth.hasCapability('pet_companion'))
const petUpgradeOpen = ref(false)

function onPetClick() {
  if (!hasPetCap.value) {
    petUpgradeOpen.value = true
    return
  }
  togglePet()
}

const tenant = computed(() => auth.session?.tenant ?? auth.me?.tenant)
const firstChar = computed(() => tenant.value?.shop_name?.charAt(0) ?? '—')
const userName = computed(() => auth.currentUser?.display_name ?? (auth.isFreeDevice ? '免费版' : ''))
const roleLabel = computed(() => {
  const r = auth.role
  return r === 'owner' ? '老板' : r === 'admin' ? '经理' : r === 'staff' ? '员工' : r === 'readonly' ? '只读' : ''
})

// ─── 一级菜单配置 ───────────────────────────────────────────────
interface SubItem {
  to: string
  name: string
  label: string
  icon: string
}
interface GroupItem {
  key: string
  label: string
  icon: string
  capability?: CapabilityName  // 未开通 → 加锁标
  to?: string                  // flat 一级(无 sub)直接 push 此 path
  subs?: SubItem[]
}

const groups: GroupItem[] = [
  {
    key: 'library',
    label: '产品图库',
    icon: 'ti-photo',
    subs: [
      { to: '/library/images', name: 'library:images', label: '图片库', icon: 'ti-folder' },
      { to: '/library/categories', name: 'library:categories', label: '分类管理', icon: 'ti-category' },
      { to: '/library/search', name: 'library:search', label: '图片搜索', icon: 'ti-search' }
    ]
  },
  {
    key: 'business',
    label: '商品管理',
    icon: 'ti-package',
    capability: 'business',
    subs: [
      { to: '/business/products',  name: 'business:products',  label: '商品库',   icon: 'ti-shopping-cart' },
      { to: '/business/inbound',   name: 'business:inbound',   label: '进货登记', icon: 'ti-arrow-down-circle' },
      { to: '/business/outbound',  name: 'business:outbound',  label: '出货登记', icon: 'ti-arrow-up-circle' },
      { to: '/business/customers', name: 'business:customers', label: '客户档案', icon: 'ti-users' },
      { to: '/business/suppliers', name: 'business:suppliers', label: '供应商档案', icon: 'ti-truck-delivery' },
      { to: '/business/reports',   name: 'business:reports',   label: '经营报表', icon: 'ti-chart-bar' }
    ]
  },
  {
    key: 'miniapp',
    label: '小程序管理',
    icon: 'ti-shopping-bag',
    capability: 'miniapp',
    subs: [
      { to: '/miniapp/dashboard',  name: 'miniapp:dashboard',  label: '运营大盘', icon: 'ti-gauge' },
      // B.51 收口:店铺装修 → 店铺管理。原独立「分类管理」并入,成为店铺管理内的「分类」tab
      // (按 C 端页面逐页配置:首页 / 分类 / 商品详情 / 公司介绍)。
      { to: '/miniapp/decoration', name: 'miniapp:decoration', label: '店铺管理', icon: 'ti-palette' },
      // "公司介绍" 在 店铺管理 → 公司介绍 tab 已有,不在 sidebar 再开重复入口。
      // 客服咨询线索(2026-06-18)退役 → 客服回归"加微信/扫码";访客/收藏走运营大盘卡下钻,不进 sidebar。
      { to: '/miniapp/usage',      name: 'miniapp:usage',      label: '使用说明', icon: 'ti-book-2' }
    ]
  },
  {
    key: 'media',
    label: '自媒体助手',
    icon: 'ti-sparkles',
    capability: 'media_assistant',
    to: '/media'
  }
  // 「系统设置」不在 groups 里 —— 它要排到「智能客服 / 电子宠物」之后(最末),
  // 而那两个是非路由按钮,只能在模板里手写,故 settings 也在模板末尾单独渲染。
]

// 哪个 group 展开(同时只能开一个;默认开"产品图库"and 跟当前 route 一致的)
const currentGroupKey = computed(() => {
  const n = (route.name as string) ?? ''
  if (n.startsWith('library:'))  return 'library'
  if (n.startsWith('business:')) return 'business'
  if (n.startsWith('miniapp:'))  return 'miniapp'
  if (n === 'media' || n.startsWith('media:')) return 'media'
  if (n === 'settings')          return 'settings'
  return 'library'
})

const expandedKey = ref(currentGroupKey.value)

// 路由变化时,自动展开当前 route 所属 group(避免点 sub link 后 group 闭合错位)
watch(currentGroupKey, (k) => {
  expandedKey.value = k
})

function toggleGroup(g: GroupItem) {
  if (g.to) {
    // flat 一级:已经被 RouterLink 处理,这里不用做事
    return
  }
  // 折叠 → 展开 的同时直接跳第一个 sub(用户心理预期"点商品管理→进商品库",
  // 不该要两次点击)。已展开时只折叠不跳(避免抢走用户当前查看的 sub)。
  if (expandedKey.value !== g.key && g.subs?.length) {
    router.push(g.subs[0].to)
  }
  expandedKey.value = expandedKey.value === g.key ? '' : g.key
}

function isGroupActive(g: GroupItem): boolean {
  return currentGroupKey.value === g.key
}

function isCapLocked(g: GroupItem): boolean {
  return !!g.capability && !auth.hasCapability(g.capability)
}

function onFlatNavClick(g: GroupItem) {
  if (g.key === 'media' && g.to && route.path !== g.to) {
    armMediaFlowHint()
  }
}

// ─── 账号菜单 (desktop-login) ────────────────────────────────────
const menuOpen = ref(false)
const loggingOut = ref(false)
// 账号菜单「点外部 / Esc 关闭」(同 ActionMenu 约定)。accountBar 同时包触发卡片+菜单。
const accountBar = ref<HTMLElement | null>(null)

function toggleMenu() {
  menuOpen.value = !menuOpen.value
}
function onAccountDocClick(e: MouseEvent) {
  if (!menuOpen.value) return
  if (accountBar.value && !accountBar.value.contains(e.target as Node)) menuOpen.value = false
}
function onAccountKey(e: KeyboardEvent) {
  if (e.key === 'Escape') menuOpen.value = false
}
onMounted(() => {
  document.addEventListener('click', onAccountDocClick)
  document.addEventListener('keydown', onAccountKey)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onAccountDocClick)
  document.removeEventListener('keydown', onAccountKey)
})

async function doLogout() {
  loggingOut.value = true
  await auth.logout()
  loggingOut.value = false
  menuOpen.value = false
  // 登出后 isAuthenticated=false → 内容区自动显 WelcomeGate,无需跳转
}
</script>

<template>
  <aside class="sidebar">
    <!-- 主菜单 -->
    <nav class="nav">
      <template v-for="g in groups" :key="g.key">
        <!-- flat 一级(无 sub):直接 RouterLink -->
        <RouterLink
          v-if="g.to"
          :to="g.to"
          class="nav-l1"
          :class="{ active: isGroupActive(g) }"
          @click="onFlatNavClick(g)"
        >
          <i :class="['ti', g.icon, 'nav-icon']" aria-hidden="true"></i>
          <span class="lbl">{{ g.label }}</span>
          <i v-if="isCapLocked(g)" class="ti ti-lock lock" aria-hidden="true" title="未开通"></i>
        </RouterLink>

        <!-- 有 sub 的 group -->
        <template v-else>
          <button
            type="button"
            class="nav-l1"
            :class="{ 'in-section': isGroupActive(g) }"
            @click="toggleGroup(g)"
          >
            <i :class="['ti', g.icon, 'nav-icon']" aria-hidden="true"></i>
            <span class="lbl">{{ g.label }}</span>
            <i v-if="isCapLocked(g)" class="ti ti-lock lock" aria-hidden="true" title="未开通"></i>
            <i
              :class="['ti', expandedKey === g.key ? 'ti-chevron-down' : 'ti-chevron-right', 'chev']"
              aria-hidden="true"
            ></i>
          </button>
          <div v-show="expandedKey === g.key" class="sub-list">
            <RouterLink
              v-for="s in g.subs"
              :key="s.to"
              :to="s.to"
              class="nav-sub"
              :class="{ active: route.name === s.name }"
            >
              <i :class="['ti', s.icon, 'nav-icon']" aria-hidden="true"></i>
              <span>{{ s.label }}</span>
            </RouterLink>
          </div>
        </template>
      </template>

      <!-- 智能客服:不是路由,点开右侧抽屉(全局挂在 AppLayout)。不门控,人人可用。
           代登录(运营只读纯浏览)时隐藏——无权操作;后端 /support/chat 另有 403 硬挡。 -->
      <button v-if="!auth.isImpersonating" type="button" class="nav-l1" @click="openSupport">
        <i class="ti ti-message-chatbot nav-icon" aria-hidden="true"></i>
        <span class="lbl">智能客服</span>
      </button>

      <!-- 电子宠物:开/关一只在窗口底边遛的小螃蟹(点它弹气泡,接 iris 的 fabric-pet host 闲聊,跟客服分开)。
           未开通 pet_companion 时加锁,点 → 升级引导(可见即诱饵)。同样代登录时隐藏。 -->
      <button
        v-if="!auth.isImpersonating"
        type="button"
        class="nav-l1"
        :class="{ active: petEnabled && hasPetCap }"
        @click="onPetClick"
      >
        <i class="ti ti-mood-smile nav-icon" aria-hidden="true"></i>
        <span class="lbl">电子宠物</span>
        <i v-if="!hasPetCap" class="ti ti-lock lock" aria-hidden="true" title="未开通"></i>
      </button>

      <!-- 系统设置:始终可用,排在最末(自媒体助手 / 智能客服 / 电子宠物 之后)。 -->
      <RouterLink
        to="/settings"
        class="nav-l1"
        :class="{ active: route.name === 'settings' }"
      >
        <i class="ti ti-settings nav-icon" aria-hidden="true"></i>
        <span class="lbl">系统设置</span>
      </RouterLink>
    </nav>

    <!-- 底部账号 / 登录栏 -->
    <div ref="accountBar" class="account-bar">
      <template v-if="auth.isAuthenticated">
        <div v-if="menuOpen" class="user-menu">
          <button type="button" class="menu-item menu-logout" :disabled="loggingOut" @click="doLogout">
            <i class="ti ti-logout" aria-hidden="true"></i>
            {{ loggingOut ? '登出中…' : '退出登录' }}
          </button>
        </div>
        <button class="tenant-card" type="button" @click="toggleMenu">
          <span class="tenant-avatar">{{ firstChar }}</span>
          <span class="tenant-meta">
            <span class="tenant-name">{{ tenant?.shop_name ?? '—' }}</span>
            <span v-if="userName" class="tenant-user">
              {{ userName }}<span v-if="roleLabel" class="role-tag">{{ roleLabel }}</span>
            </span>
          </span>
          <i :class="['ti', menuOpen ? 'ti-chevron-up' : 'ti-chevron-down', 'chev']" aria-hidden="true"></i>
        </button>
      </template>
      <button v-else class="login-bar" type="button" @click="openAuth('login')">
        <i class="ti ti-login-2" aria-hidden="true"></i>
        <span>登录</span>
      </button>
    </div>

    <UpgradeModal
      :open="petUpgradeOpen"
      title="电子宠物 · 未开通"
      message="开通后,桌面会有一只小螃蟹陪你唠嗑 —— 跟智能客服分开,随便聊、有记忆。每天有一定免费额度,用完可让店主加量。"
      @close="petUpgradeOpen = false"
    />
  </aside>
</template>

<style scoped>
.sidebar {
  background: var(--color-background-tertiary);
  border-right: 0.5px solid var(--color-border-tertiary);
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ─── 底部账号 / 登录栏 ─── */
.account-bar {
  padding: 8px 10px 0;
  border-top: 0.5px solid var(--color-border-tertiary);
  margin-top: 8px;
  flex-shrink: 0;
}
.login-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  padding: 9px 8px;
  background: var(--color-brand);
  color: #fff;
  border: none;
  border-radius: var(--border-radius-md);
  font: inherit;
  font-size: var(--fs-control);
  font-weight: var(--fw-med);
  cursor: pointer;
}
.login-bar:hover {
  filter: brightness(1.06);
}
.tenant-card {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: var(--color-background-primary);
  border: var(--border-width-control) solid var(--color-border-control);
  border-radius: var(--border-radius-md);
  cursor: pointer;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  transition: background 0.15s;
}
.tenant-card:hover {
  background: var(--color-background-secondary);
}
.tenant-avatar {
  width: 18px;
  height: 18px;
  border-radius: var(--border-radius-sm);
  background: #EEEDFE;
  color: #3C3489;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: var(--fw-med);
}
.tenant-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.tenant-name {
  font-size: var(--fs-label);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tenant-user {
  font-size: 9px;
  color: var(--color-text-tertiary);
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.role-tag {
  font-size: 8px;
  padding: 0 4px;
  border-radius: var(--border-radius-pill);
  background: var(--color-background-info);
  color: var(--color-text-info);
}

.user-menu {
  margin-bottom: 6px;
  padding: 4px;
  background: var(--color-background-primary);
  border: 0.5px solid var(--color-border-tertiary);
  border-radius: var(--border-radius-md);
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 7px 8px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius-sm, 4px);
  font: inherit;
  font-size: var(--fs-control);
  color: var(--color-text-primary);
  cursor: pointer;
  text-align: left;
}
.menu-item:hover:not(:disabled) {
  background: var(--color-background-secondary);
}
.menu-item:disabled {
  opacity: 0.6;
  cursor: default;
}
.menu-logout {
  color: var(--color-text-danger, #c0392b);
}

/* ─── 主菜单 ─── */
.nav {
  padding: 0 8px;
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* 共同基线：所有可点条目(一级 / 二级)共享的盒子 + 过渡 */
.nav-l1,
.nav-sub {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: transparent;
  border-radius: var(--border-radius-md);
  text-decoration: none;
  color: inherit;
  text-align: left;
  font: inherit;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
}
.nav-l1 .nav-icon,
.nav-sub .nav-icon { flex-shrink: 0; }
.nav-l1 .lbl,
.nav-sub span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── 一级：所有顶层条目统一(有无子级、是否带锁都同款) ── */
.nav-l1 {
  padding: 7px 10px;
  font-size: var(--fs-item);            /* 13 */
  font-weight: var(--fw-med);           /* 500 */
  color: var(--color-text-primary);
}
.nav-l1 .nav-icon {
  font-size: 17px;
  color: var(--color-text-tertiary);
}
.nav-l1:hover { background: var(--color-background-secondary); }
.nav-l1:hover .nav-icon { color: var(--color-text-secondary); }

/* 当前所在分区的父级:文字变深表示"你在这一区",但不抢二级的实心高亮 */
.nav-l1.in-section,
.nav-l1.in-section .nav-icon { color: var(--color-text-primary); }

/* 当前页(无子级的扁平条目自身就是路由) */
.nav-l1.active { background: var(--color-background-info); }
.nav-l1.active,
.nav-l1.active .nav-icon { color: var(--color-text-info); }

/* ── 二级:左导引线 + 缩进 + 更小更浅,与一级拉开层级 ── */
.sub-list {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 2px 0 6px 18px;
  padding-left: 9px;
  border-left: 1px solid var(--color-border-tertiary);
}
.nav-sub {
  padding: 6px 9px;
  font-size: var(--fs-control);         /* 12 */
  font-weight: 400;
  color: var(--color-text-secondary);
}
.nav-sub .nav-icon { font-size: 15px; }
.nav-sub:hover {
  background: var(--color-background-secondary);
  color: var(--color-text-secondary);
}
.nav-sub.active {
  background: var(--color-background-info);
  color: var(--color-text-info);
  font-weight: var(--fw-med);
}

/* ── 角标:展开箭头 + 未开通锁 ── */
.chev {
  font-size: var(--fs-control);
  color: var(--color-text-tertiary);
  margin-left: auto;
  flex-shrink: 0;
}
.lock {
  font-size: var(--fs-label);
  color: var(--color-text-warning);
  margin-left: 4px;
  flex-shrink: 0;
}

</style>
